import {
    ChartSettingsT,
    ColorMapping,
    DataTypes,
    OrderedColumnsT,
} from 'business/definitions';
import { Dataset, SeriePointT } from '../../types';
import PolymorphicChartOption from './PolymorphicChartOption';
import {
    datasetEncode,
    DEFAULT_DATASET_NAME,
    extractSeriesKey,
    getDatasetValueKey,
    makeDataset,
} from 'components/ChartContainer/components/helpers/datasetHelpers';
import { EChartsOption } from 'echarts';
import { getChartLegendSettings } from 'helpers/chart/legendHelpers';
import { isDictAllDataValueT, isFontSize, setIfDefined } from './helpers';
import { formatDisplay } from 'helpers/formatHelper';
import uniq from 'lodash/uniq';
import { METRIC_KEY } from 'helpers/dataHelpers/pivot';
import { columnOrder } from '../TableChart/defaultSettings';
import {
    formatAxisLabel,
    formatTooltipTitle,
    getAxisType,
    getTooltipString,
    isContinuous,
    translateTreeLabel,
} from 'helpers/chartHelper';
import { getMinValue } from 'helpers/axis/xAxis';
import { UNKNOWN_DATUM } from 'business/constants';
import { camelize, snakify } from 'helpers/syntaxHelper';
import { getMappedColor } from '../helpers/getColor';
import {
    EChartsSerie,
    FontSize,
    SerieLabel,
    TreeValueTranslationsT,
} from './types';
import {
    getYAxisNameGap,
    getYMaxValueFormatter,
    wrapYAxisName,
} from 'helpers/axis/yAxis';
import {
    LARGE_AXIS_LABEL_FONT_SIZE,
    MEDIUM_AXIS_LABEL_FONT_SIZE,
    SMALL_AXIS_LABEL_FONT_SIZE,
} from 'helpers/axis/commons';
import {
    wrap,
    XAxisHorizontalWrapFactor,
    YAxisHorizontalWrapFactor,
} from 'helpers/labels/wrapLabels';
import { translateBlock } from 'i18n';
import { getGridSettings } from 'helpers/chart/gridHelpers';
import {
    getBins,
    getInterval,
    getTooltipPosition,
    sortSeriesForYearlyComparision,
} from 'helpers/eChartHelpers';
import { getDataZoom } from 'helpers/chart/dataZoom';
import { getGoalValueColor } from 'helpers/chart/goalValue';
import { getProjectedSerieType } from '../helpers/projection';
import { isDateOrYear } from 'helpers/helpers';

// FIXME Il y a un bug sans conséquence: Lorsque l'on selectionne le type de graphique
// Si l'on passe la souris très vite dessus, echart a une valeur null en tooltip et ressort une erreur
// C'est sans conséquence car ça ne fait rien crash et les tooltips vont s'afficher

export type CartesianChartType = 'line' | 'scatter' | 'bar';

const DEFAULT_DECIMAL_PRECISION = 2;
const SECONDARY_AXIS_INDEX = 1;

/**
 * Base class for all cartesian (X/Y) charts.
 */
export abstract class CartesianChartOption extends PolymorphicChartOption {
    protected dataZoomInterval: [number, number] | undefined;
    protected isPdfModeActivated: boolean;
    protected isPngModeActivated: boolean;
    protected isChartViewedOnDashboard: boolean;

    // Computer when making the dataset.
    protected interval?: number;
    protected ticks?: number[];

    // We sometime need to access the translations from outside
    // so we make it public.
    treeValueTranslations: TreeValueTranslationsT;

    constructor(
        orderedColumns: OrderedColumnsT,
        settings: ChartSettingsT,
        data: ResponseDataT[],
        width: number,
        height: number,
        dataZoomInterval: [number, number] | undefined,
        isPdfModeActivated: boolean,
        isPngModeActivated: boolean,
        isChartViewedOnDashboard: boolean,
    ) {
        super(orderedColumns, settings, data, width, height);
        this.dataZoomInterval = dataZoomInterval;
        this.isPdfModeActivated = isPdfModeActivated;
        this.isPngModeActivated = isPngModeActivated;
        this.isChartViewedOnDashboard = isChartViewedOnDashboard;
        this.treeValueTranslations = this.getTreeValueTranslations();
        const { interval, ticks } = this.getIntervalAndTick();
        this.interval = interval;
        this.ticks = ticks;
    }

    /**
     * Retunr the interval between to values in data. Useful for raw data request when data is aggregated on xAxis
     */
    public getInterval() {
        return this.interval;
    }

    protected shouldHaveInterval(type: DataTypesT): boolean {
        return (
            type === DataTypes.integer ||
            type === DataTypes.year ||
            isContinuous(type)
        );
    }

    /**
     * Return the interval (distance between value to display) and the ticks/bins
     * for the data of the x axis.
     * TODO: Check that ticks are indeed useless and remove them.
     */
    protected getIntervalAndTick(): {
        interval: number | undefined;
        ticks: number[] | undefined;
    } {
        if (this.orderedColumns.x === undefined) {
            return { interval: undefined, ticks: undefined };
        }
        const { type, scale, key } = this.orderedColumns.x;
        const hasInterval = this.shouldHaveInterval(type);
        if (!hasInterval) {
            return { interval: undefined, ticks: undefined };
        }
        // We can cast as number because of the previous condition.
        // Integer, date and continuous type are always number.
        const xValues = uniq(
            this.data.map((dataPoint) => dataPoint[key]),
        ).sort() as number[];
        const interval = getInterval(type, scale, xValues);
        const ticks = getBins(type, scale, xValues);
        return {
            interval,
            ticks,
        };
    }

    /**
     * {@see PolymorphicChartOption.getDataset} for the original documentation.
     * We reimplement it to handle the yearly comparision option which exists for cartesian chart.
     * We also generate the interval and ticks here.
     */
    protected override getDataset(): Dataset[] {
        const seriesKey = extractSeriesKey(this.data, this.orderedColumns);
        return makeDataset(
            this.data,
            this.orderedColumns,
            seriesKey,
            this.settings.isYearlyComparison,
            this.interval,
            this.settings?.sortDimension,
        );
    }

    getChartOption(): EChartsOption {
        let option = super.getChartOption();
        const series = this.getSeries();
        option = setIfDefined(option, 'grid', this.getGrid(series));
        option = setIfDefined(option, 'dataZoom', this.getDatazoom());
        option = setIfDefined(option, 'legend', this.getLegend());
        option = setIfDefined(option, 'tooltip', this.getTooltip());
        option = setIfDefined(option, 'series', series);
        option = setIfDefined(option, 'xAxis', this.getXAxis());
        option = setIfDefined(option, 'yAxis', this.getYAxis());
        return option;
    }

    /**
     * Override this function in the children class to output the
     * EChart chart type.
     */
    protected abstract getChartType(): CartesianChartType;

    /**
     * Helper function to return the choosen decimal precision
     * (how many decimal point we display) or the default value.
     */
    protected getDecimalPrecision(): 0 | 1 | 2 {
        if (this.settings.decimalPrecision === undefined) {
            return DEFAULT_DECIMAL_PRECISION;
        }
        return this.settings.decimalPrecision;
    }

    /**
     * Return the custom color mapping, which link a dimension with a color.
     */
    protected getCustomColor(serieName: string | undefined): ColorMapping[] {
        let customColor: ColorMapping[] = [];
        const customColorLegends =
            this.settings.customPalette && this.settings.palette
                ? this.settings.customPalette[this.settings.palette]
                : undefined;
        if (customColorLegends !== undefined && serieName !== undefined) {
            const dimensionKey = this.orderedColumns.z
                ? this.orderedColumns.z.key
                : this.orderedColumns.y.key;
            const transformedKey = camelize(snakify(dimensionKey));
            customColor =
                customColorLegends[dimensionKey] ||
                customColorLegends[transformedKey];
        }
        return customColor;
    }

    /**
     * Return the EChart legend option.
     */
    protected getLegend(): EChartsOption['legend'] {
        const isExportModeActivated =
            this.isPdfModeActivated || this.isPngModeActivated;
        return getChartLegendSettings({
            isExportModeActivated,
            showLegend: this.settings.showLegend,
            showLegendOnRightSide: this.settings.showLegendOnRightSide,
            width: this.width,
            treeValueTranslations: this.treeValueTranslations,
        });
    }

    protected getDatasetValueKey(point: SeriePointT): string {
        return getDatasetValueKey(point);
    }

    /**
     * Used for tooltip and label: Return the formatted version of a datapoint in a serie.
     * This function has to handle verious cases such as: Tree value (usually orgChart),
     * numeral value, and when the X axis is time, the evolution of the value compared
     * to the previous value.
     * @param dataPoint - The serie data point / value to format.
     */
    protected getFormattedSerieValue(
        dataPoint: SeriePointT,
        showEvolution: boolean,
    ) {
        const serieValue = dataPoint.value[
            this.getDatasetValueKey(dataPoint)
        ] as number | string;
        let formattedValue = formatDisplay(
            serieValue,
            this.orderedColumns.y.type,
            this.getDecimalPrecision(),
            this.settings.displayBy,
        );

        // Find the previous value to compute the evolution.
        // For this we need to find the corresponding dataset.
        // When there is no Z axis, we should only have 1 dataset (called 'base')
        // so we take the first dataset.
        // When there is a Z axis, we find the dataset corresponding to the series
        // of the data point.
        let selectedDataset;
        if (this.orderedColumns.z) {
            selectedDataset = this.dataset.find(
                (ds) => ds.id === dataPoint.seriesId,
            );
        } else {
            selectedDataset = this.dataset[0];
        }
        const previousPoint =
            selectedDataset?.source?.[dataPoint.dataIndex - 1];
        if (showEvolution && previousPoint) {
            const previousValue = previousPoint[
                this.getDatasetValueKey(dataPoint)
            ] as number | string;
            const isValidPreviousValue =
                previousValue !== 0 &&
                previousValue !== UNKNOWN_DATUM &&
                typeof serieValue === 'number' &&
                typeof previousValue === 'number';
            if (isValidPreviousValue) {
                const evolution = (serieValue - previousValue) / previousValue;
                const formattedTimeEvolution =
                    evolution > 0
                        ? `+${formatDisplay(
                              evolution,
                              'fraction',
                              this.getDecimalPrecision(),
                              this.settings.displayBy,
                          )}`
                        : `${formatDisplay(
                              evolution,
                              'fraction',
                              this.getDecimalPrecision(),
                              this.settings.displayBy,
                          )}`;
                formattedValue = `${formattedValue} (${formattedTimeEvolution})`;
            }
        }

        return {
            label:
                translateTreeLabel(
                    dataPoint.seriesName,
                    this.treeValueTranslations,
                ) || dataPoint.seriesName,
            marker: dataPoint.marker,
            value: formattedValue,
        };
    }

    /**
     * Used when generating tooltip: Format the value of all series
     * displayed in the tooltip.
     * @param params - The series data point to format. Passed by EChart.
     */
    protected getFormattedSeriesValues(params: SeriePointT[]) {
        const value = params[0].axisValue;
        const title = formatTooltipTitle(
            value,
            this.orderedColumns.x!.type,
            this.orderedColumns.x?.scale,
            this.interval,
            this.treeValueTranslations,
        );
        // We only display the evolution % when the X axis is a time
        // and when we are not doing a pluri-annual comparision.
        const isYearlyComparision = this.settings.isYearlyComparison ?? false;
        const showEvolution =
            isDateOrYear(this.orderedColumns.x?.type) && !isYearlyComparision;
        const tooltipSeries = params.map((d) =>
            this.getFormattedSerieValue(d, showEvolution),
        );
        return getTooltipString(title.toString(), tooltipSeries);
    }

    /**
     * Return the EChart tooltip configuration.
     */
    protected getTooltip(): EChartsOption['tooltip'] {
        // @ts-expect-error [typing] The formatter excpect a certain type.
        return {
            transitionDuration: 0,
            position(
                pos: number[],
                params: object[],
                el: Element,
                elRect: object,
                size: object,
            ) {
                if (el === null || el === undefined) {
                    return undefined;
                }
                return getTooltipPosition(pos, params, el, elRect, size);
            },
            trigger: 'axis',
            axisPointer: {
                type: 'line',
            },
            formatter: (params: SeriePointT[]) =>
                `<div class='echart-helper-tooltips'>
                    ${this.getFormattedSeriesValues(params)}
                </div>`,
        };
    }

    /**
     * Return the EChart grid configuration. EChart normally use this to
     * display several chart in the same space. We never use it like this,
     * for us this is mostly a way to manage the padding and margin of the
     * chart so that its various label and legend don't overlap.
     * @param series - The series used in this chart. Should be from {@see this.getSeries}.
     */
    protected getGrid(series: EChartsSerie[]): EChartsOption['grid'] {
        // we add space above the chart so that the legend is't mixed
        // with the top of the chart on PDF
        let legendExtraSpace;
        if (series.length > 6) {
            legendExtraSpace = 35;
        } else if (series.length > 3) {
            legendExtraSpace = 20;
        } else {
            legendExtraSpace = 0;
        }

        return getGridSettings({
            isExportModeActivated:
                this.isPdfModeActivated || this.isPngModeActivated,
            legendExtraSpace,
            isDataZoomShown:
                !!this.settings.isDataZoomEnabled &&
                !(
                    this.isChartViewedOnDashboard ||
                    this.isPdfModeActivated ||
                    this.isPngModeActivated
                ),
            showLegend: !!this.settings.showLegend,
            showLegendOnRightSide: !!this.settings.showLegendOnRightSide,
            width: this.width,
            verticalAxisWidth: undefined,
            goalValue: this.settings.goalValue,
        });
    }

    /**
     * Return the serie label configuration. This govern how EChart will
     * display the serie value on the chart when the option to show the values
     * is activated.
     * @param serie - The serie to generate the config for.
     * @param serieIndex - The index of this serie in the order that it is passed to echart
     * (basically equal to the index on a forEach on this.getSeries).
     */
    protected getLabel(serie: EChartsSerie, serieIndex: number): SerieLabel {
        // We only show the % of evolution if the option is activated and
        // we are not a pluriannual comparision:
        const showEvolution =
            !!this.settings.isEvolutionDisplayed &&
            !this.settings.isYearlyComparison;
        const label: SerieLabel = {
            show: this.settings.areLabelsShown,
            textBorderWidth: 0,
            textBorderColor: 'white',
            formatter: (d: SeriePointT) =>
                this.getFormattedSerieValue(d, showEvolution).value,
            rotate: this.settings.areLabelsVertical ? 0 : 90,
        };

        label['color'] = getMappedColor(
            serie.name,
            serieIndex,
            this.getCustomColor(serie.name),
            this.getColor(),
            this.settings.useGradientColor,
        );

        if (this.settings.areLabelsShownInside) {
            label['position'] = 'inside';
        }

        // TODO TechDebt: This setting is flipped:
        // it is true when the labels are horizontal.
        // We should fix the name or the value.
        if (!this.settings.areLabelsVertical) {
            label['verticalAlign'] = 'middle';
            label['align'] = 'left';
        }

        return label;
    }

    /**
     * Return the echart's series. Each serie defined which dataset it takes its data from,
     * what it display on each axis, etc.
     * When we don't have a groupement/dimension, it should output only one serie for the
     * request metric.
     */
    protected getSeries(): EChartsSerie[] {
        let series: EChartsSerie[] = this.generateBaseSeries();

        // The goal line / objectif / markline is defined for each series and not on the axis.
        series = this.handleSeriesGoalValue(series);

        // Handle the double Y axis if it is activated.
        series = this.handleSeriesDoubleYAxis(series);

        // When we have a yearly comparision, we have to sort the series
        // by month or year (depending on the conception).
        if (this.settings.isYearlyComparison) {
            // @ts-expect-error [typing] Expect the type SerieT but we use EChartsSerie.
            sortSeriesForYearlyComparision(series, this.orderedColumns);
        }

        // IMPROVEMENT: Colors are dependent on the serie index. This means that
        // we should only compute anything color related once the serie's list
        // is on its final sort. This is why we generate label at the end.
        // But we cannot ensure this on child classes so it has cognitive load
        // and it's easy to introduce a bug.
        // Therefore, we should make series colors somehow not index-related.
        //
        // @ts-expect-error [typing] Label are not defined for all echart,
        // so the typing system panic because it cannot validate that we will
        // always have an object that conform to echart composite type.
        // There is no consequence to ignore this, as echart wont do anything
        // if the chart has value it does not use.
        series = series.map((s, i) => ({
            ...s,
            label: this.getLabel(s, i),
        }));

        return series;
    }

    /**
     * Generate the base series array.
     * When we have a Z axis, it will be 1 serie for each dataset,
     * otherwise, just one serie.
     */
    private generateBaseSeries(): EChartsSerie[] {
        // When we have a Z axis, we generate one serie for each Z value.
        if (this.orderedColumns.z) {
            const series = this.dataset.map((dataset) => ({
                id: dataset.id,
                datasetId: dataset.id,
                encode: datasetEncode(this.dataset, this.orderedColumns),
                name: formatDisplay(
                    dataset.id,
                    this.orderedColumns.z!.type,
                    undefined,
                    undefined,
                    this.orderedColumns.z!.scale,
                ),
                type: this.getChartType(),
            }));

            // We always sort by the serie's name first, then, if we are a multimetric request
            // (meaning we want a serie for each metric) we also sort by the by the index of the column.
            // IMPORTANT: We *need* to do both sort if it is a multimetric request, because the result of doing
            // just the multimetric sort is not the same as doing both. It would change the series order and break
            // the default color on some chart, so we are stuck with that.
            series.sort((a, b) => {
                if (a.name === undefined) return b.name === undefined ? 0 : -1;
                if (b.name === undefined) return 1;
                return a.name.localeCompare(b.name);
            });
            if (this.orderedColumns.z.key === METRIC_KEY) {
                series.sort((a, b) => {
                    const aIndex = columnOrder.indexOf(a.id as string);
                    const bIndex = columnOrder.indexOf(b.id as string);
                    return aIndex - bIndex;
                });
            }
            return series;
        } else {
            // There is only one serie when we have no Z axis.
            return [
                {
                    id: this.orderedColumns.y.key,
                    datasetId: DEFAULT_DATASET_NAME,
                    encode: datasetEncode(this.dataset, this.orderedColumns),
                    name: this.orderedColumns.y.label,
                    type: this.getChartType(),
                },
            ];
        }
    }

    /**
     * Add the goal value (if needed) to the series.
     */
    protected handleSeriesGoalValue(series: EChartsSerie[]): EChartsSerie[] {
        if (this.settings.goalValue === undefined) {
            return series;
        }
        // @ts-expect-error [typing] TODO see what is triggering the type error.
        return series.map((s) => {
            const markLine = {
                symbol: 'none',
                data: [{ yAxis: this.settings.goalValue }],
                lineStyle: {
                    type: 'solid',
                    color: getGoalValueColor(
                        !!this.settings.useGradientColor,
                        !!this.settings.reverseGradientColor,
                        this.settings.palette,
                        this.getColor(),
                    ),
                },
                label: {
                    formatter: () =>
                        formatDisplay(
                            this.settings.goalValue,
                            this.orderedColumns.y.type,
                            this.getDecimalPrecision(),
                            this.settings.displayBy,
                        ),
                },
            };
            return {
                ...s,
                markLine,
            };
        });
    }

    /**
     * Handle the double Y axis on the series, if it is needed.
     */
    protected handleSeriesDoubleYAxis(series: EChartsSerie[]): EChartsSerie[] {
        if (
            this.settings.secondaryYAxis !== undefined &&
            this.orderedColumns.x !== undefined
        ) {
            const secondaryAxisSerieIdx = series.findIndex(
                (serie) => serie.id === this.settings.secondaryYAxis,
            );
            if (secondaryAxisSerieIdx > -1) {
                // @ts-expect-error [typing] TODO see what is triggering the type error.
                return series.map((serie, index) => {
                    if (index === secondaryAxisSerieIdx) {
                        return {
                            ...serie,
                            yAxisIndex: SECONDARY_AXIS_INDEX,
                            type: getProjectedSerieType(
                                this.orderedColumns.x!.type,
                            ),
                        };
                    }
                    return serie;
                });
            }
        }
        return series;
    }

    /**
     * Generate the "treeValueTranslations" which is an object used
     * when dealing with tree data, like orgChart.
     * It will generate an object which will have all the value of the tree
     * (usually service code) as key, and the label (human readable name for the service)
     * as the value.
     */
    public getTreeValueTranslations(): TreeValueTranslationsT {
        const treeValueTranslations: Record<string, string> = {};
        // For each data point, we look at the x,y,z value.
        // If it is a tree object (DictAllDataValueT, a.k.a it's an object
        // with a label and value), we add the value has a treeValueTranslation
        // key, and its label has the value.
        this.data.forEach((d) => {
            Object.keys(d).forEach((key) => {
                const value = d[key];
                if (!isDictAllDataValueT(value)) {
                    return;
                }
                if (value.value instanceof Date) {
                    console.error(
                        `treeValueTranslations error: data ${value} has a date value, which cannot be used as index.`,
                    );
                    return;
                }
                if (typeof value.value === 'boolean') {
                    return;
                }
                if (value?.value !== undefined && value?.label !== undefined) {
                    treeValueTranslations[value.value] = value.label;
                }
            });
        });
        return treeValueTranslations;
    }

    /**
     * Handle the "data zoom", a.k.a, the sliding window that is used
     * to display a specific range of value of the chart.
     */
    protected getDatazoom(): EChartsOption['dataZoom'] {
        if (!this.settings.isDataZoomEnabled) {
            return undefined;
        }

        return getDataZoom({
            isHorizontal: false,
            show: !(
                this.isChartViewedOnDashboard ||
                this.isPdfModeActivated ||
                this.isPngModeActivated
            ),
            decimalPrecision: this.getDecimalPrecision(),
            displayBy: this.settings.displayBy,
            treeValueTranslations: this.treeValueTranslations,
            xType: this.orderedColumns.x!.type,
            xScale: this.orderedColumns.x!.scale,
            xInterval: this.interval,
            color: this.getColor(),
            dataZoomInterval: this.dataZoomInterval,
        });
    }

    /**
     * Return the font size depending on preset values, or
     * a default value for unknown value.
     */
    private getPresetFontSize(size: string): number {
        const fontSizeMap: Record<FontSize, number> = {
            sm: SMALL_AXIS_LABEL_FONT_SIZE,
            md: MEDIUM_AXIS_LABEL_FONT_SIZE,
            lg: LARGE_AXIS_LABEL_FONT_SIZE,
        };
        if (isFontSize(size)) {
            return fontSizeMap[size];
        }

        return MEDIUM_AXIS_LABEL_FONT_SIZE;
    }

    /**
     * Return the optimal font size depending on if the dataset contains
     * at least one label with more than 15 chars, or if it doesn't contains
     * a long label but contains a short (<5 chars) label.
     * return a default value if both condition are not met.
     */
    protected getFontSizeFromDatasetLabelSize(axisKey: string) {
        // Find if we have long and short label
        let hasShortLabel = false;
        for (const dataset of this.dataset) {
            // Some dataset are just transform dataset used to sort the data.
            // In this case they don't have any data and we should skip them.
            if (!('source' in dataset)) {
                continue;
            }
            for (const dataPoint of dataset.source) {
                let valueForAxis = dataPoint[axisKey];
                if (valueForAxis === null || valueForAxis === undefined) {
                    continue;
                }
                // When we have tree data, we want to measure the label.
                if (isDictAllDataValueT(valueForAxis)) {
                    valueForAxis = valueForAxis.label;
                }
                // When we have at least one datapoint with a long value
                // we selected the smaller size.
                if (valueForAxis.toString().length > 15) {
                    return SMALL_AXIS_LABEL_FONT_SIZE;
                }
                // If we have short label, we will select the large size
                // but only if we have no long label, so we need to check all
                // data.
                if (valueForAxis.toString().length < 5) {
                    hasShortLabel = true;
                }
            }
        }
        return hasShortLabel
            ? LARGE_AXIS_LABEL_FONT_SIZE
            : MEDIUM_AXIS_LABEL_FONT_SIZE;
    }

    /**
     * Compute the axis label font size.
     * We have quite a lot of rule to define the size of the axis label.
     * When it is explicitly defined, we just map to a specific size, but
     * when the "AUTO" option is used (the default), we try to guess the appropriate
     * size depending on the size of the value to display, but only for category.
     */
    protected getAxisLabelFontSize(axis: ResponseColumnT) {
        if (
            this.settings.axisLabelFontSize !== undefined &&
            this.settings.axisLabelFontSize !== 'default'
        ) {
            return this.getPresetFontSize(this.settings.axisLabelFontSize);
        }

        if (getAxisType(axis.type) !== 'category') {
            return MEDIUM_AXIS_LABEL_FONT_SIZE;
        }

        return this.getFontSizeFromDatasetLabelSize(axis.key);
    }

    /**
     * Compute the gap between the X axis line and its label.
     */
    protected getXAxisNameGap(): number {
        if (this.settings.areTicksRotated) {
            return 50;
        }
        // Find the longest label on the x axis.
        if (
            this.orderedColumns.x === undefined ||
            this.orderedColumns.x.key === undefined
        ) {
            return 20;
        }
        const { key } = this.orderedColumns.x;
        let longuestLabel = '';
        for (const dataset of this.dataset) {
            // {@see getFontSizeFromDatasetLabelSize} for the explanation of this if.
            if (!('source' in dataset)) {
                continue;
            }
            for (const dataPoint of dataset.source) {
                const value = dataPoint[key];
                if (
                    isDictAllDataValueT(value) &&
                    value.label.length > longuestLabel.length
                ) {
                    longuestLabel = value.label;
                } else if (value.toString().length > longuestLabel.length) {
                    longuestLabel = value.toString();
                }
            }
        }
        const wrapped = wrap(
            longuestLabel,
            this.width,
            XAxisHorizontalWrapFactor,
            4,
        );
        const lines = (wrapped.match(/\n/g) || []).length;
        return 20 + lines * 10;
    }

    /**
     * Map our own internal axis type to EChart supported axis type
     * for the x axis.
     */
    protected getXAxisType(): 'category' | 'time' | 'value' {
        return getAxisType(this.orderedColumns.x!.type);
    }

    protected getXAxisLabelInterval(): 0 | 'auto' {
        const isCategory = this.getXAxisType() === 'category';
        // Check that we only have less than 15 labels.
        let hasLessThan15Labels = true;
        for (const dataset of this.dataset) {
            // {@see getFontSizeFromDatasetLabelSize} for the explanation of this if.
            if (!('source' in dataset)) {
                continue;
            }
            hasLessThan15Labels =
                hasLessThan15Labels && dataset.source.length < 15;
        }

        // 0 ensure that we always display all the x axis value, even when the chart is small.
        // auto handle the case where we have a lot of column.
        return isCategory && !hasLessThan15Labels ? 'auto' : 0;
    }

    /**
     * Return the X axis configuration.
     */
    protected getXAxis(): EChartsOption['xAxis'] {
        const xAxisLabels: Record<string, string | number | null | undefined> =
            {};
        if (this.orderedColumns.x === undefined) {
            return undefined;
        }
        const { type, scale, label } = this.orderedColumns.x;
        const xAxisType = this.getXAxisType();

        let xAxis: EChartsOption['xAxis'] = {
            // @ts-expect-error [typing] Due to how echart define its type, it cannot recognize our type.
            // even though its valid.
            type: xAxisType,
            scale: true,
            boundaryGap: ['5%', '5%'],
            name: this.settings.xAxisTitle || label,
            nameLocation: 'middle',
            nameGap: this.getXAxisNameGap(),
            nameTextStyle: {
                color: '#666',
                fontFamily: 'Poppins',
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: '#666666',
                },
            },
            splitArea: { show: false },
            splitLine: { show: false },
        };

        if (xAxisType === 'value') {
            xAxis = {
                ...xAxis,
                min: ({ min, max }: { min: number; max: number }) =>
                    getMinValue(min, max),
            };
        }

        // Generate the axisLabel object. We separate it for readability because
        // it has many rule that are not evident.
        const axisLabel = {
            fontFamily: 'Poppins',
            fontSize: this.getAxisLabelFontSize(this.orderedColumns.x),
            rotate: this.settings.areTicksRotated ? 30 : 0,
            align: this.settings.areTicksRotated ? 'right' : 'center',
            interval: this.getXAxisLabelInterval(),
        };

        // Handle interval and ticks.
        // We only use them if we have a reasonable amount of them.
        // TODO Find if the ticks option ever existed.
        if (this.ticks && this.ticks.length < 15 && this.interval) {
            xAxis = {
                ...xAxis,
                interval: this.interval,
            };
        }

        const formatter = (value: string | undefined): string => {
            const formatted = formatAxisLabel(
                value,
                type,
                this.getDecimalPrecision(),
                this.settings.displayBy,
                this.interval,
                scale,
                this.settings.areTicksRotated,
                this.treeValueTranslations,
            ).toString();
            if (xAxisType === 'time') {
                // Remove duplicated labels when the X axis is the time
                // It is a known issue
                // https://github.com/apache/echarts/issues/9896
                if (
                    formatted in xAxisLabels &&
                    value !== xAxisLabels[formatted]
                )
                    return '';
                xAxisLabels[formatted] = value;
            }

            return wrap(formatted, this.width, XAxisHorizontalWrapFactor, 4);
        };

        xAxis = {
            ...xAxis,
            axisLabel: {
                ...axisLabel,
                // @ts-expect-error [typing] the echart type seems to only accept number
                // value as input, but we do receive string.
                formatter,
            },
        };

        return xAxis;
    }

    /**
     * Return the Y axis configuration.
     * We can potentially have 2 Y axis, so this function might return
     * an array with the axis object.
     */
    protected getYAxis(): EChartsOption['yAxis'] {
        const firstYAxis: EChartsOption['yAxis'] = {
            name: wrapYAxisName(
                this.settings.yAxisTitle || this.orderedColumns.y.label,
            ),
            nameGap: getYAxisNameGap(this.settings.areTicksRotated, false),
            nameLocation: 'end',
            nameTextStyle: {
                color: '#666',
                fontFamily: 'Poppins',
            },
            max: getYMaxValueFormatter(
                false,
                this.orderedColumns.y.type,
                this.settings.goalValue,
            ),
            axisLabel: {
                fontSize: this.getAxisLabelFontSize(this.orderedColumns.y),
                fontFamily: 'Poppins',
                color: '#666',
                formatter: (value: number) =>
                    wrap(
                        formatDisplay(
                            value,
                            this.orderedColumns.y.type,
                            this.getDecimalPrecision(),
                            this.settings.displayBy,
                        ),
                        this.width,
                        YAxisHorizontalWrapFactor,
                        1,
                    ),
            },
            axisLine: { show: false },
            axisTick: { show: false },
        };

        if (!this.settings.secondaryYAxis) {
            return firstYAxis;
        }

        const secondaryYAxis: EChartsOption['yAxis'] = {
            ...firstYAxis,
            name: translateBlock(this.settings.secondaryYAxis),
            max: undefined,
            axisLabel: {
                ...firstYAxis.axisLabel,
                align: 'left',
            },
        };

        return [firstYAxis, secondaryYAxis];
    }
}
